import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: value });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset link. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <Card className="w-full max-w-md p-8" data-testid="forgot-password-card">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-blue-600 text-white flex items-center justify-center">
            <FileText size={18} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500">Account recovery</div>
            <h1 className="text-xl font-bold tracking-tight">Forgot password</h1>
          </div>
        </div>

        {sent ? (
          <div className="space-y-5" data-testid="forgot-password-sent">
            <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-md p-4">
              <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-emerald-900">
                If an account exists for <b>{email}</b>, we've sent a reset link.
                Check your inbox (and spam) for an email from us. The link expires
                in <b>30 minutes</b>.
              </div>
            </div>
            <Button
              asChild
              variant="outline"
              className="w-full h-10"
              data-testid="forgot-back-to-login"
            >
              <Link to="/login">
                <ArrowLeft size={14} className="mr-2" />
                Back to sign in
              </Link>
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-zinc-500 mb-5">
              Enter the email on your account and we'll send a secure link to set a new password.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Email</Label>
                <Input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 mt-1 text-sm"
                  placeholder="you@company.in"
                  data-testid="forgot-email"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 h-10"
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : (
                  <Mail size={16} className="mr-2" />
                )}
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <div className="text-xs text-zinc-500 text-center mt-6">
              <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="link-back-login">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
